import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Link2, Loader2, RotateCw, Wallet } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" }) : "—");

function PaymentStatus({ offer }) {
  const p = offer.payment || {};
  if (offer.paid === "paid") return <span className="text-emerald-700 font-semibold">Paid in full {p.amount_paid ? money(p.amount_paid) : ""}{p.method === "pay_later" ? " · via pay later" : ""}</span>;
  return <span className={offer.expired ? "text-red-600 font-semibold" : "text-slate-700"}>{offer.expired ? "Link expired" : `Link sent · expires ${when(offer.expires_at)}`}</span>;
}

export default function ResidencyOfferPanel({ appId }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get(`/admin/applications/${appId}/offer`).then((r) => {
    setD(r.data);
    setF((prev) => prev || { amount: r.data.offer?.amount ?? r.data.program_price ?? "", allow_pay_later: true });
  }), [appId]);
  useEffect(() => { load(); }, [load]);
  if (!d || !f) return null;

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); } finally { setBusy(""); }
  };
  const send = () => run("send", () => api.post(`/admin/applications/${appId}/payment-link`, {
    amount: Number(f.amount), allow_pay_later: f.allow_pay_later,
  }), "Payment link emailed (expires in 72h)");
  const action = (a) => run(a, () => api.post(`/admin/applications/${appId}/payment-link/${a}`), "Link renewed for 72h and re-sent");
  const o = d.offer;
  const done = o?.paid === "paid";

  return (
    <div data-testid={`admin-residency-offer-${appId}`} className="rounded-xl border border-blue-200 bg-blue-50/40 p-4 space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <CalendarDays size={14} className="text-blue-800" />
        <span className="font-semibold text-slate-900">Intake call:</span>
        <span data-testid={`admin-residency-intake-${appId}`}>{d.intake ? when(d.intake.start) : d.invited_at ? `invite sent ${when(d.invited_at)} · not booked yet` : "not invited"}</span>
      </div>
      {o && (
        <div className="flex flex-wrap items-center gap-2" data-testid={`admin-residency-payment-${appId}`}>
          <Wallet size={14} className="text-blue-800" /> <span className="font-semibold text-slate-900">Payment:</span> <PaymentStatus offer={o} />
          {!done && (
            <Button size="sm" variant="outline" className="h-7" disabled={!!busy} onClick={() => action("extend")} data-testid={`admin-residency-extend-${appId}`}>
              <RotateCw size={12} className="mr-1" /> Resend & extend 72h
            </Button>
          )}
        </div>
      )}
      {!o?.paid && (
        <div className="flex flex-wrap items-end gap-3" data-testid={`admin-residency-offer-form-${appId}`}>
          <label className="text-xs font-semibold text-slate-600">Program price (USD)
            <Input type="number" className="h-9 w-32 mt-1" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid={`admin-residency-amount-${appId}`} /></label>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 pb-2">
            <Switch checked={f.allow_pay_later} onCheckedChange={(v) => setF({ ...f, allow_pay_later: v })} data-testid={`admin-residency-allow-pay-later-${appId}`} /> Pay later (Klarna / Afterpay / Affirm)</label>
          <Button size="sm" className="h-9 bg-blue-900 hover:bg-blue-800" disabled={!!busy || !f.amount} onClick={send} data-testid={`admin-residency-send-link-${appId}`}>
            {busy === "send" ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Link2 size={13} className="mr-1.5" />} {o ? "Send new link" : "Send payment link"}
          </Button>
        </div>
      )}
    </div>
  );
}
